import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import {
  Button,
  Checkbox,
  ConfirmModal,
  Input,
  Label,
  Modal,
  ModalFooter,
  Textarea,
} from "@/components/ui";
import { CountrySelect } from "@/components/ui/phone";
import { useCountryOptions } from "@/hooks/useCountryOptions";
import {
  clampPresencePeopleInput,
  padTimeString,
  parseUtcDateTime,
  PRESENCE_PEOPLE_MAX,
  PRESENCE_PEOPLE_MIN,
} from "@/lib/devicePresence";
import { toastApiError } from "@/lib/errorMessages";
import { presenceGuestDisplayName } from "@/lib/presenceDisplay";
import { housePresenceKeys, houseService } from "@/services/houseService";
import { deviceKeys } from "@/services/deviceService";
import type {
  DevicePresenceUpdate,
  HouseCurrentPresenceItem,
  RegionCode,
} from "@/types";

interface EditHousePresenceModalProps {
  houseId: string;
  presence: HouseCurrentPresenceItem;
  /** Host payment region for this house tablet (always set on house routes). */
  regionCode: RegionCode;
  onClose: () => void;
  onDeleted?: () => void;
}

export function EditHousePresenceModal({
  houseId,
  presence,
  regionCode,
  onClose,
  onDeleted,
}: EditHousePresenceModalProps) {
  const { t: tComponents } = useTranslation("components");
  const { t } = useTranslation("houses");
  const { t: tCommon } = useTranslation("common");
  const queryClient = useQueryClient();
  const countryOptions = useCountryOptions();

  const [guestName, setGuestName] = useState(
    presence.guest_name?.trim() ?? "",
  );
  const [nationality, setNationality] = useState(
    presence.nationality.trim() || regionCode,
  );
  const [arrivalDate, setArrivalDate] = useState(presence.arrival_date);
  const [arrivalTime, setArrivalTime] = useState(
    padTimeString(presence.arrival_time || "00:00"),
  );
  const [departureDate, setDepartureDate] = useState(presence.departure_date);
  const [departureTime, setDepartureTime] = useState(
    padTimeString(presence.departure_time || "23:59"),
  );
  const [numPeopleInput, setNumPeopleInput] = useState(
    String(presence.num_people),
  );
  const [hasChildren, setHasChildren] = useState(presence.has_children);
  const [notes, setNotes] = useState(presence.notes?.trim() ?? "");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const invalidatePresenceLists = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: housePresenceKeys.current(houseId),
      }),
      queryClient.invalidateQueries({
        queryKey: housePresenceKeys.upcoming(houseId),
      }),
      queryClient.invalidateQueries({
        queryKey: housePresenceKeys.history(houseId),
      }),
      queryClient.invalidateQueries({
        queryKey: ["kiosk-presences", houseId],
      }),
      queryClient.invalidateQueries({
        queryKey: deviceKeys.byHouse(houseId),
      }),
    ]);
  };

  const updateMutation = useMutation({
    mutationFn: (data: DevicePresenceUpdate) =>
      houseService.updateDevicePresence(
        houseId,
        presence.device_id,
        presence.id,
        data,
      ),
    onSuccess: async () => {
      await invalidatePresenceLists();
      toast.success(t("kioskPresence.toastUpdated"));
      onClose();
    },
    onError: (err: unknown) => {
      toastApiError(err, "updatePresence");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      houseService.deleteDevicePresence(
        houseId,
        presence.device_id,
        presence.id,
      ),
    onSuccess: async () => {
      await invalidatePresenceLists();
      setConfirmDelete(false);
      toast.success(t("kioskPresence.toastDeleted"));
      onDeleted?.();
      onClose();
    },
    onError: (err: unknown) => {
      toastApiError(err, "deletePresence");
    },
  });

  const isBusy = updateMutation.isPending || deleteMutation.isPending;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const trimmedGuestName = guestName.trim();
    const trimmedNationality = nationality.trim();

    if (!trimmedGuestName) {
      toast.error(t("kioskPresence.errorGuestNameRequired"));
      return;
    }
    if (!trimmedNationality) {
      toast.error(t("kioskPresence.errorNationalityRequired"));
      return;
    }
    if (!arrivalDate || !departureDate) {
      toast.error(t("kioskPresence.errorDatesRequired"));
      return;
    }

    const arrival = parseUtcDateTime(arrivalDate, arrivalTime);
    const departure = parseUtcDateTime(departureDate, departureTime);
    if (departure <= arrival) {
      toast.error(t("kioskPresence.errorDepartureAfterArrival"));
      return;
    }

    const data: DevicePresenceUpdate = {
      guest_name: trimmedGuestName,
      nationality: trimmedNationality,
      arrival_date: arrivalDate,
      arrival_time: arrivalTime,
      departure_date: departureDate,
      departure_time: departureTime,
      num_people: parseInt(clampPresencePeopleInput(numPeopleInput), 10),
      has_children: hasChildren,
      notes: notes.trim() || undefined,
    };
    updateMutation.mutate(data);
  };

  return (
    <>
      <ConfirmModal
        closeLabel={tCommon("close")}
        cancelText={tCommon("cancel")}
        isOpen={confirmDelete}
        onClose={() => {
          if (deleteMutation.isPending) return;
          setConfirmDelete(false);
        }}
        onConfirm={() => {
          deleteMutation.mutate();
        }}
        title={tCommon("delete")}
        message={t("kioskPresence.deleteConfirm", {
          guestName: presenceGuestDisplayName(
            presence.guest_name,
            t("detail.presences.genericGuestName"),
          ),
        })}
        confirmText={tCommon("delete")}
        isDestructive
        isLoading={deleteMutation.isPending}
      />

      <Modal
        closeLabel={tCommon("close")}
        isOpen
        onClose={() => {
          if (isBusy) return;
          onClose();
        }}
        title={t("detail.presences.editModalTitle", {
          deviceName:
            presence.device_room_name?.trim() || t("changeRoomNoRoom"),
        })}
        size="lg"
        footer={
            <ModalFooter className="sm:justify-between">
              <Button
                type="button"
                variant="outline"
                className="border-error-200 text-error-700 hover:border-error-300 hover:bg-error-50 hover:text-error-800 dark:hover:bg-error-50"
                onClick={() => setConfirmDelete(true)}
                disabled={isBusy}
              >
                {tCommon("delete")}
              </Button>
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isBusy}
                >
                  {tCommon("cancel")}
                </Button>
                <Button
                  type="submit"
                  form="edit-house-presence-form"
                  isLoading={updateMutation.isPending}
                  disabled={!guestName.trim() || !nationality.trim() || isBusy}
                >
                  {t("kioskPresence.buttonUpdatePresence")}
                </Button>
              </div>
            </ModalFooter>
        }
      >
        <form id="edit-house-presence-form" onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="edit-presence-guest-name">
                {t("kioskPresence.labelGuestName")}
              </Label>
              <Input
                id="edit-presence-guest-name"
                value={guestName}
                onChange={(event) => setGuestName(event.target.value)}
                placeholder={t("kioskPresence.placeholderGuestName")}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-presence-nationality">
                {t("kioskPresence.labelNationality")}
              </Label>
              <CountrySelect
                searchPlaceholderLabel={tComponents("countrySelectSearchPlaceholder")}
                searchInputAriaLabel={tComponents("countrySelectSearchPlaceholder")}
                noResultsLabel={tComponents("countrySelectNoResults")}
                id="edit-presence-nationality"
                value={nationality}
                onChange={setNationality}
                placeholder={t("kioskPresence.placeholderNationality")}
                countries={countryOptions}
                defaultCountry={regionCode}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="edit-presence-arrival-date">
                {t("kioskPresence.labelArrivalDate")}
              </Label>
              <Input
                id="edit-presence-arrival-date"
                type="date"
                value={arrivalDate}
                onChange={(event) => setArrivalDate(event.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-presence-arrival-time">
                {t("kioskPresence.labelArrivalTime")}
              </Label>
              <Input
                id="edit-presence-arrival-time"
                type="time"
                value={arrivalTime}
                onChange={(event) => setArrivalTime(event.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="edit-presence-departure-date">
                {t("kioskPresence.labelDepartureDate")}
              </Label>
              <Input
                id="edit-presence-departure-date"
                type="date"
                value={departureDate}
                onChange={(event) => setDepartureDate(event.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-presence-departure-time">
                {t("kioskPresence.labelDepartureTime")}
              </Label>
              <Input
                id="edit-presence-departure-time"
                type="time"
                value={departureTime}
                onChange={(event) => setDepartureTime(event.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="edit-presence-num-people">
                {t("kioskPresence.labelPeople")}
              </Label>
              <Input
                id="edit-presence-num-people"
                type="number"
                min={PRESENCE_PEOPLE_MIN}
                max={PRESENCE_PEOPLE_MAX}
                value={numPeopleInput}
                onChange={(event) => setNumPeopleInput(event.target.value)}
                onBlur={() =>
                  setNumPeopleInput((current) => clampPresencePeopleInput(current))
                }
                onFocus={(event) => event.currentTarget.select()}
                className="w-24"
              />
            </div>
            <div className="flex items-center gap-2 sm:pt-6">
              <Checkbox
                id="edit-presence-has-children"
                checked={hasChildren}
                onChange={(event) => setHasChildren(event.target.checked)}
              />
              <Label htmlFor="edit-presence-has-children" className="text-sm">
                {t("kioskPresence.labelChildrenCheckbox")}
              </Label>
            </div>
          </div>

          <div>
            <Label htmlFor="edit-presence-notes">{t("kioskPresence.labelNotes")}</Label>
            <Textarea
              id="edit-presence-notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder={t("kioskPresence.placeholderNotes")}
              rows={2}
            />
          </div>
        </form>
      </Modal>
    </>
  );
}
